/* eslint-disable standard/computed-property-even-spacing */
import Vue from 'vue'
import axios from 'axios'
import JwtService from '@/common/jwt.service'
import { API_URL } from '@/common/config'
import { LOGOUT } from '../store/actions.type.js'
import { MessageBox, Message } from 'element-ui'
import { REFRESH_FAILED_FLASH_MESSAGE } from '@/router/routeGuards'

// Internal only - nothing outside this file imports it by name.
const axiosInstanceForRefresh = axios.create({
  baseURL: API_URL,
  responseType: 'json'
})

// Deduplicates concurrent/repeated refresh attempts across every 401 that fires while the
// session is dead (e.g. an idle background poller) - without this, each one independently
// re-attempts a refresh and re-dispatches LOGOUT/LOGOUT_CLIENT, hammering the backend.
// Concurrent 401s share this one in-flight promise instead of each starting their own; and
// once the refresh token is cleared, refreshAccessToken() short-circuits before calling the
// backend at all rather than sending a token-less request.
let inFlightRefresh = null

// Guards against a burst of concurrent 401s each firing their own flash message + redirect.
// Re-armed (below) as soon as a real refresh token exists again, so a session established
// *after* a forced logout - admin logs back in without a page reload - can still be terminated
// later. A never-reset latch would silently disable this for the rest of the page's life.
let sessionTerminationHandled = false

/**
 * Purges the session locally and sends the user to the login screen.
 *
 * Deliberately never calls a backend logout endpoint (unlike the client's own explicit logout
 * button, which does, via LOGOUT_CLIENT): by the time we're here the token is confirmed dead, so
 * any authenticated call would just 401 again - and that 401 would re-enter this same shared
 * axios interceptor while `inFlightRefresh` still points at the promise we're resolving, a
 * genuine circular await that deadlocks forever (found live when a rotated JWT secret killed
 * every session at once). Same reasoning handleWithdrawalViolation (common/api.service.js) uses.
 */
const terminateDeadSession = () => {
  if (sessionTerminationHandled) {
    return
  }
  sessionTerminationHandled = true

  // Remove the expired token from the shared axios instance's default headers.
  delete Vue.axios.defaults.headers.common['Authorization']

  const host = window.location.hostname
  const DOMAIN_ADMIN = process.env.VUE_APP_DOMAIN_ADMIN
  if (host === DOMAIN_ADMIN) {
    // PURGE_AUTH also calls JwtService.destroyAll(), so this clears storage as well as Vuex.
    window.app.$store.dispatch(LOGOUT)
    Message({ message: REFRESH_FAILED_FLASH_MESSAGE, type: 'warning', duration: 5000 })
    window.app.$router.push({ name: 'login' })
  } else {
    JwtService.destroyAll()
    Message({ message: REFRESH_FAILED_FLASH_MESSAGE, type: 'warning', duration: 5000 })
    // Reuses routeMember.js's own sessionViolation query param - its beforeEach guard opens
    // the login dialog only after CHECK_AUTH_CLIENT has settled for this navigation, the
    // same clobbering-avoidance mechanism handleWithdrawalViolation already relies on.
    window.app.$router.push({ path: '/portal', query: { sessionViolation: '1' } })
  }
}

const RefreshTokenService = {

  // 再度、同じリクエストを行うためにerr.configを渡す。
  async getNewAccessToken (config) {
    const accessToken = await RefreshTokenService.refreshAccessToken()
    if (accessToken) {
      config.headers.Authorization = accessToken
      // ヘッダーのアクセストークンを新しいものに変更して再度同じリクエストを行う。
      // interceptorsは、実行させたくないのでアクセストークンの再取得で使用したインスタンスを利用する。
      return axiosInstanceForRefresh.request(config)
    }
    return false
  },

  // Deduplicated network refresh attempt (see the module-level comment above). Resolves with
  // the new access token string on success, or `false` on any failure, including having no
  // refresh token to try at all.
  refreshAccessToken () {
    if (inFlightRefresh) {
      return inFlightRefresh
    }

    const refreshToken = JwtService.getRefreshToken()
    if (!refreshToken) {
      // No refresh token to try - the cookie expired (its max-age is
      // VUE_APP_REFRESH_TOKEN_EXPIRE_SEC) or was destroyed. Before this short-circuit existed,
      // the doomed `Bearer undefined` request that used to be sent here came back 401 and fell
      // into the .catch() below, which is what logged the user out. Skipping the request without
      // also terminating the session left the UI "logged in" against a dead token - every data
      // call 401ing forever behind an empty shell, with no way back to the login screen.
      terminateDeadSession()
      return Promise.resolve(false)
    }

    // A real refresh token exists again (fresh login, or a still-live cookie), so allow a future
    // failure to terminate the session once more.
    sessionTerminationHandled = false

    inFlightRefresh = axiosInstanceForRefresh.get('/token/getAccessToken', {
      headers: { Authorization: `Bearer ${refreshToken}` }
    }).then((response) => {
      // 正常系
      const apiToken = response.headers['api-token']
      JwtService.saveToken(apiToken)
      // リフレッシュトークンの取得と保存
      const newRefreshToken = response.headers['api-refresh-token']
      JwtService.saveRefreshToken(newRefreshToken)
      const newToken = JwtService.getToken()
      Vue.axios.defaults.headers.common['Authorization'] = `${newToken}`
      return `${newToken}`
    }).catch(async (error) => {
      // レスポンスが正常系でない場合にログアウトする。
      terminateDeadSession()

      const host = window.location.hostname
      const DOMAIN_ADMIN = process.env.VUE_APP_DOMAIN_ADMIN
      if (error.response && error.response.status !== 401) {
        // それ以外のエラーの場合
        if (host === DOMAIN_ADMIN && error.response.status >= 500) {
          MessageBox({
            title: 'エラーが発生しました。(エラーコード: ' + error.response.status + ')',
            type: 'エラー',
            showClose: false,
            center: true
          })
        }
      }
      return false
    }).finally(() => {
      inFlightRefresh = null
    })

    return inFlightRefresh
  },

  // Test-only: module-level state persists across test cases in the same Jest worker otherwise.
  __resetInFlightRefreshForTests () {
    inFlightRefresh = null
    sessionTerminationHandled = false
  }
}

export default RefreshTokenService
